export type LocationAdminLevel =
  | 'city'
  | 'department'
  | 'region'
  | 'country'
  | 'other';

const CITY_TYPES = new Set([
  'locality',
  'postal_town',
  'sublocality',
  'sublocality_level_1',
]);

/**
 * Countries where Google maps provinces/municipalities to admin_level_1
 * the same way (no separate "region" layer like France).
 * For these, admin1 should use city/proximity search (radius), not
 * hierarchy region search.
 *
 * FR: admin1 = Île-de-France → true region (no radius)
 * VN: admin1 = Đà Nẵng / Hà Tĩnh / HCMC → city-like (radius)
 */
const ADMIN1_AS_PROXIMITY_COUNTRY_CODES = new Set([
  'VN', // Vietnam
]);

const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  vietnam: 'VN',
  'viet nam': 'VN',
  'việt nam': 'VN',
  france: 'FR',
};

function namesMatch(left?: string, right?: string): boolean {
  if (!left || !right) {
    return false;
  }
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function getAddressComponent(
  components: google.maps.GeocoderAddressComponent[] = [],
  type: string,
): google.maps.GeocoderAddressComponent | undefined {
  return components.find((part) => part.types.includes(type));
}

function resolveCountryCode(
  addressComponents: google.maps.GeocoderAddressComponent[] = [],
  fallbackCountryName?: string,
): string | undefined {
  const fromComponents = getAddressComponent(
    addressComponents,
    'country',
  )?.short_name;
  if (fromComponents) {
    return fromComponents.toUpperCase();
  }
  if (!fallbackCountryName) {
    return undefined;
  }
  return COUNTRY_NAME_TO_CODE[fallbackCountryName.trim().toLowerCase()];
}

/**
 * Centrally-governed cities (e.g. Da Nang) may be tagged only as
 * administrative_area_level_1 without locality.
 */
function isMunicipalityAdminLevel1(
  types: string[] = [],
  addressComponents: google.maps.GeocoderAddressComponent[] = [],
): boolean {
  if (!types.includes('administrative_area_level_1')) {
    return false;
  }
  if (types.some((type) => CITY_TYPES.has(type))) {
    return true;
  }
  const admin1 = getAddressComponent(
    addressComponents,
    'administrative_area_level_1',
  );
  const locality =
    getAddressComponent(addressComponents, 'locality') ||
    getAddressComponent(addressComponents, 'postal_town');
  return namesMatch(admin1?.long_name, locality?.long_name);
}

function shouldTreatAdmin1AsProximityCity(
  countryCode?: string,
): boolean {
  return !!countryCode && ADMIN1_AS_PROXIMITY_COUNTRY_CODES.has(countryCode);
}

/**
 * Resolve administrative level from Google Places types (+ optional
 * address_components / country hint).
 *
 * Google trigger differences:
 * - FR Paris: types include locality → city
 * - FR Île-de-France: admin_level_1 only → region
 * - VN HCMC: often locality (+ admin1) → city
 * - VN Đà Nẵng / Hà Tĩnh: often admin_level_1 only → must use country rule
 *   (VN admin1 = province/municipality → city/proximity, not FR-style region)
 */
export function resolveAdminLevel(
  types: string[] = [],
  addressComponents: google.maps.GeocoderAddressComponent[] = [],
  fallbackCountryName?: string,
  /** Autocomplete main_text — required to avoid "Da Nang, Vietnam" → country. */
  placePrimaryName?: string,
): LocationAdminLevel {
  if (types.includes('country')) {
    return 'country';
  }
  // Place Details for "France" sometimes omit type=country and only
  // expose address_components.country (no city/region). Must not fall
  // through to "other" → radius around country centroid (0 users).
  if (
    isCountryOnlyPlace(
      types,
      addressComponents,
      fallbackCountryName,
      placePrimaryName,
    )
  ) {
    return 'country';
  }
  if (types.some((type) => CITY_TYPES.has(type))) {
    return 'city';
  }
  if (isMunicipalityAdminLevel1(types, addressComponents)) {
    return 'city';
  }
  if (types.includes('administrative_area_level_1')) {
    const countryCode = resolveCountryCode(
      addressComponents,
      fallbackCountryName,
    );
    if (shouldTreatAdmin1AsProximityCity(countryCode)) {
      return 'city';
    }
    return 'region';
  }
  if (types.includes('administrative_area_level_2')) {
    return 'department';
  }
  return 'other';
}

/**
 * Resolve ISO country code from name/components (e.g. France → FR).
 */
export function resolveCountryCodeFromName(
  countryName?: string,
): string | undefined {
  if (!countryName?.trim()) {
    return undefined;
  }
  return COUNTRY_NAME_TO_CODE[countryName.trim().toLowerCase()];
}

function isCountryOnlyPlace(
  types: string[],
  addressComponents: google.maps.GeocoderAddressComponent[],
  fallbackCountryName?: string,
  placePrimaryName?: string,
): boolean {
  const hasCityComponent =
    !!getAddressComponent(addressComponents, 'locality') ||
    !!getAddressComponent(addressComponents, 'postal_town');
  const hasAdmin1 = !!getAddressComponent(
    addressComponents,
    'administrative_area_level_1',
  );
  const hasAdmin2 = !!getAddressComponent(
    addressComponents,
    'administrative_area_level_2',
  );
  const hasCountryComponent = !!getAddressComponent(
    addressComponents,
    'country',
  );
  if (addressComponents.length > 0) {
    return (
      hasCountryComponent &&
      !hasCityComponent &&
      !hasAdmin1 &&
      !hasAdmin2
    );
  }
  // Autocomplete (no components): "France" may be types=["political"]
  // only. Do NOT treat "Da Nang, Vietnam" as country just because the
  // description ends with a known country name.
  const hasCityOrAdminType =
    types.some((type) => CITY_TYPES.has(type)) ||
    types.includes('administrative_area_level_1') ||
    types.includes('administrative_area_level_2');
  if (hasCityOrAdminType) {
    return false;
  }
  if (!resolveCountryCode([], fallbackCountryName)) {
    return false;
  }
  return namesMatch(placePrimaryName, fallbackCountryName);
}

export function formatAdminLevelLabel(
  level: LocationAdminLevel,
): string {
  switch (level) {
    case 'city':
      return 'City';
    case 'region':
      return 'Region';
    case 'department':
      return 'Department';
    case 'country':
      return 'Country';
    default:
      return 'Place';
  }
}

/**
 * Resolve the display name for the selected admin level.
 * Examples: Paris | Île-de-France | France
 */
export function resolveLevelDisplayName(
  level: LocationAdminLevel,
  parts: {
    city?: string;
    department?: string;
    region?: string;
    country?: string;
    fallback?: string;
  },
): string {
  switch (level) {
    case 'country':
      return parts.country?.trim() || parts.fallback?.trim() || '';
    case 'region':
      return parts.region?.trim() || parts.fallback?.trim() || '';
    case 'department':
      return (
        parts.department?.trim() ||
        parts.region?.trim() ||
        parts.fallback?.trim() ||
        ''
      );
    case 'city':
    default:
      return (
        parts.city?.trim() ||
        parts.fallback?.trim() ||
        ''
      );
  }
}

/**
 * Autocomplete / history primary title (selected admin level name).
 * Examples: Paris | Île-de-France | France
 */
export function formatLocationSuggestionPrimary(
  level: LocationAdminLevel,
  parts: {
    city?: string;
    department?: string;
    region?: string;
    country?: string;
    fallback?: string;
  },
): string {
  return resolveLevelDisplayName(level, parts);
}

/**
 * Autocomplete / history secondary line (muted hierarchy path).
 * Example: Paris, Île-de-France, France
 * Do NOT include department — it pollutes VN paths (Bà Nà) and is not
 * in the ticket display examples.
 */
export function formatLocationSuggestionDetail(parts: {
  city?: string;
  department?: string;
  region?: string;
  country?: string;
}): string {
  const first = parts.city || parts.department;
  const values = [first, parts.region, parts.country]
    .map((value) => value?.trim())
    .filter((value): value is string => !!value);
  const unique: string[] = [];
  for (const value of values) {
    if (!unique.some((entry) => entry.toLowerCase() === value.toLowerCase())) {
      unique.push(value);
    }
  }
  return unique.join(', ');
}

/**
 * Build primary + detail lines for suggestion / history rows.
 */
export function formatLocationSuggestionLines(input: {
  adminLevel?: LocationAdminLevel;
  city?: string;
  department?: string;
  region?: string;
  country?: string;
  name?: string;
  formattedAddress?: string;
  suggestionDetail?: string;
  hierarchy?: {
    city?: { name?: string };
    department?: { name?: string };
    region?: { name?: string };
    country?: { name?: string };
  };
}): { primary: string; detail: string } {
  const level = input.adminLevel || 'city';
  const city = input.hierarchy?.city?.name || input.city;
  const department =
    input.hierarchy?.department?.name || input.department;
  const region = input.hierarchy?.region?.name || input.region;
  const country = input.hierarchy?.country?.name || input.country;
  const primary = formatLocationSuggestionPrimary(level, {
    city,
    department,
    region,
    country,
    fallback: input.name || input.formattedAddress,
  });
  const hierarchyDetail = formatLocationSuggestionDetail({
    city,
    department,
    region,
    country,
  });
  // Autocomplete secondary can be only "Vietnam" under primary "Đà Nẵng".
  // Never prefer that alone after select / for filter chip.
  const rawDetail = input.suggestionDetail?.trim() || '';
  const rawIsCountryOnly =
    !!rawDetail &&
    !!country &&
    rawDetail.toLowerCase() === country.trim().toLowerCase();
  const rawMissingPrimary =
    !!rawDetail &&
    !!primary &&
    !rawDetail.toLowerCase().includes(primary.toLowerCase()) &&
    rawIsCountryOnly;
  let detail = hierarchyDetail;
  if (
    rawDetail &&
    !rawIsCountryOnly &&
    !rawMissingPrimary
  ) {
    detail = rawDetail;
  }
  if (!detail && input.formattedAddress) {
    detail = input.formattedAddress;
  }
  if (
    detail &&
    primary &&
    detail.toLowerCase() === primary.toLowerCase()
  ) {
    detail = '';
  }
  return { primary, detail };
}

/**
 * Card display label: "City, Region"
 * Examples: Paris, Île-de-France | Barcelona, Catalonia
 * Never uses country as the second part (avoids "Paris, France").
 */
export function formatLocationCityRegion(
  hierarchy?: {
    city?: { name?: string };
    department?: { name?: string };
    region?: { name?: string };
    country?: { name?: string };
  },
  fallbackName?: string,
): string {
  const fallback = fallbackName?.trim() || '';
  const fallbackParts = fallback
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const fallbackCountry =
    fallbackParts.length >= 2
      ? fallbackParts[fallbackParts.length - 1]
      : undefined;
  const city =
    hierarchy?.city?.name?.trim() ||
    (fallbackParts.length > 0 ? fallbackParts[0] : undefined);
  const department = hierarchy?.department?.name?.trim();
  const country =
    hierarchy?.country?.name?.trim() || fallbackCountry;
  const regionRaw = hierarchy?.region?.name?.trim();
  const regionFromName =
    fallbackParts.length >= 3
      ? fallbackParts[1]
      : fallbackParts.length === 2 && hierarchy?.country?.name
        ? fallbackParts[1]
        : undefined;
  const regionCandidate = regionRaw || regionFromName;
  const region =
    regionCandidate &&
    regionCandidate.toLowerCase() !== country?.toLowerCase() &&
    regionCandidate.toLowerCase() !== city?.toLowerCase()
      ? regionCandidate
      : undefined;
  if (city && region) {
    return `${city}, ${region}`;
  }
  if (!city && department && region) {
    return `${department}, ${region}`;
  }
  if (city) {
    return city;
  }
  if (department) {
    return department;
  }
  if (region) {
    return region;
  }
  if (!fallback) {
    return '';
  }
  // "Paris, Île-de-France, France" → City, Region
  if (fallbackParts.length >= 3) {
    return `${fallbackParts[0]}, ${fallbackParts[1]}`;
  }
  // "Paris, France" → city only (never show country as region)
  if (fallbackParts.length === 2) {
    return fallbackParts[0];
  }
  return fallback;
}

/**
 * Persistable location.name: "City, Region, Country".
 * Prefer hierarchy so cards/detail never fall back to Google
 * "Paris, France" when region is known.
 */
export function buildLocationStorageName(
  hierarchy?: {
    city?: { name?: string };
    department?: { name?: string };
    region?: { name?: string };
    country?: { name?: string };
  },
  fallbackName?: string,
): string {
  const country = hierarchy?.country?.name?.trim();
  const regionRaw = hierarchy?.region?.name?.trim();
  const region =
    regionRaw &&
    regionRaw.toLowerCase() !== country?.toLowerCase()
      ? regionRaw
      : undefined;
  const parts = [
    hierarchy?.city?.name?.trim() || hierarchy?.department?.name?.trim(),
    region,
    country,
  ].filter(Boolean) as string[];
  const unique: string[] = [];
  for (const part of parts) {
    const exists = unique.some(
      (item) => item.toLowerCase() === part.toLowerCase(),
    );
    if (!exists) {
      unique.push(part);
    }
  }
  if (unique.length >= 2) {
    return unique.join(', ');
  }
  return fallbackName?.trim() || unique[0] || '';
}

/**
 * Legacy single-line label (internal/debug):
 * - Paris · City · France
 */
export function formatHierarchicalSuggestionLabel(
  name: string,
  level: LocationAdminLevel,
  country?: string,
): string {
  const levelLabel = formatAdminLevelLabel(level);
  const placeName = name?.trim() || '';
  const countryLabel = country?.trim();
  if (!placeName) {
    return '';
  }
  if (level === 'country' || !countryLabel || countryLabel === placeName) {
    return `${placeName} · ${levelLabel}`;
  }
  return `${placeName} · ${levelLabel} · ${countryLabel}`;
}

export function isProximitySearchLevel(
  level: LocationAdminLevel,
): boolean {
  return level === 'city' || level === 'other';
}

/**
 * Infer admin level from stored hierarchy when profile has no adminLevel.
 */
export function inferAdminLevelFromHierarchy(hierarchy?: {
  city?: { name?: string };
  department?: { name?: string };
  region?: { name?: string };
  country?: { name?: string };
}): LocationAdminLevel | undefined {
  if (!hierarchy) {
    return undefined;
  }
  if (hierarchy.city?.name) {
    return 'city';
  }
  if (hierarchy.department?.name) {
    return 'department';
  }
  if (hierarchy.region?.name) {
    return 'region';
  }
  if (hierarchy.country?.name) {
    return 'country';
  }
  return undefined;
}

/**
 * Explore / invite header lines:
 * - Line 1: Paris - 40km (city) or Île-de-France (region)
 * - Line 2: Paris, Île-de-France, France
 */
export function formatLocationFilterDisplayLines(
  locality?: {
    city?: string;
    country?: string;
    region?: string;
    adminLevel?: LocationAdminLevel;
    suggestionDetail?: string;
    hierarchy?: {
      city?: { name?: string };
      department?: { name?: string };
      region?: { name?: string };
      country?: { name?: string };
    };
  },
  radiusMeter?: number,
): { primary: string; detail: string } {
  if (!locality) {
    return { primary: '', detail: '' };
  }
  const lines = formatLocationSuggestionLines(locality);
  const level = locality.adminLevel || 'city';
  let primary = lines.primary;
  if (
    isProximitySearchLevel(level) &&
    radiusMeter != null &&
    radiusMeter > 0 &&
    primary
  ) {
    primary = `${primary} - ${radiusMeter / 1000}km`;
  }
  return { primary, detail: lines.detail };
}

/**
 * Single-line display for search inputs after select:
 * full hierarchy path, e.g. "Paris, Île-de-France, France"
 * (city mode may append " - 40km").
 */
export function formatLocationFilterDisplayLabel(
  locality?: {
    city?: string;
    country?: string;
    region?: string;
    adminLevel?: LocationAdminLevel;
    suggestionLabel?: string;
    suggestionDetail?: string;
    suggestionPrimary?: string;
    formattedAddress?: string;
    hierarchy?: {
      city?: { name?: string };
      department?: { name?: string };
      region?: { name?: string };
      country?: { name?: string };
    };
  },
  radiusMeter?: number,
): string {
  if (!locality) {
    return '';
  }
  const level = locality.adminLevel || 'city';
  const selectedPrimary = locality.suggestionPrimary?.trim();
  const hierarchyCity = locality.hierarchy?.city?.name?.trim();
  const isSelectedPlaceWithinCity =
    !!selectedPrimary &&
    !!hierarchyCity &&
    selectedPrimary.toLowerCase() !== hierarchyCity.toLowerCase();
  if (isProximitySearchLevel(level) && isSelectedPlaceWithinCity) {
    const placeLabel =
      locality.formattedAddress ||
      locality.suggestionDetail ||
      selectedPrimary;
    if (radiusMeter == null || radiusMeter === 0) {
      return placeLabel;
    }
    return `${placeLabel} - ${radiusMeter / 1000}km`;
  }
  const city =
    locality.hierarchy?.city?.name ||
    locality.suggestionPrimary ||
    locality.city;
  const hierarchyPath = formatLocationSuggestionDetail({
    city,
    region: locality.hierarchy?.region?.name,
    country: locality.hierarchy?.country?.name || locality.country,
  });
  const { primary, detail } = formatLocationFilterDisplayLines(
    {
      ...locality,
      // Force rebuilt path; ignore autocomplete leftover "Vietnam".
      suggestionDetail: undefined,
      city,
    },
    radiusMeter,
  );
  const fullPath =
    hierarchyPath ||
    detail ||
    primary ||
    locality.formattedAddress ||
    '';
  if (!isProximitySearchLevel(level)) {
    return fullPath;
  }
  if (radiusMeter == null || radiusMeter === 0) {
    return fullPath;
  }
  return `${fullPath} - ${radiusMeter / 1000}km`;
}
